import { motion } from 'framer-motion';
import { 
  Instagram, 
  Facebook, 
  Youtube, 
  Globe, 
  Phone, 
  MessageCircle,
  ChevronRight
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Bio = () => {
  const links = [
    {
      name: 'Explore Our Website',
      url: 'https://masterhome.co.in/',
      icon: <Globe className="w-5 h-5" />,
      color: 'bg-primary-600',
      description: 'Visit our official website for full details'
    },
    {
      name: 'WhatsApp Us',
      url: 'https://wa.me/919567810944',
      icon: <MessageCircle className="w-5 h-5" />,
      color: 'bg-[#25D366]',
      description: 'Direct chat with our experts'
    },
    {
      name: 'Follow on Instagram',
      url: 'https://www.instagram.com/masterhome_builders/',
      icon: <Instagram className="w-5 h-5" />,
      color: 'bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]',
      description: 'See our latest projects and designs'
    },
    {
      name: 'Facebook Page',
      url: 'https://www.facebook.com/Buildersmattannur/',
      icon: <Facebook className="w-5 h-5" />,
      color: 'bg-[#1877F2]',
      description: 'Join our growing community'
    },
    {
      name: 'Subscribe on YouTube',
      url: 'https://www.youtube.com/@masterhomeindia',
      icon: <Youtube className="w-5 h-5" />,
      color: 'bg-[#FF0000]',
      description: 'Watch our construction walkthroughs'
    },
    {
      name: 'Call Us Now',
      url: 'tel:+919567810944',
      icon: <Phone className="w-5 h-5" />,
      color: 'bg-gray-800',
      description: 'Talk to us for immediate assistance'
    },
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-start pt-16 pb-20 px-4 relative overflow-hidden font-sans">
      <Helmet>
        <title>Master Home Builders | Connect With Us</title>
        <meta name="description" content="Connect with Master Home Builders on social media, visit our website, or contact us directly." />
      </Helmet>

      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full -z-10 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-primary-50 rounded-full blur-[100px] opacity-40 animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-primary-100/30 rounded-full blur-[100px] opacity-40 animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md text-center"
      >
        {/* Profile Image/Logo */}
        <div className="mb-8 relative flex justify-center">
          <motion.div 
            whileHover={{ scale: 1.05, rotate: 5 }}
            className="w-28 h-28 bg-white rounded-3xl p-4 shadow-2xl border border-white/50 relative z-10 overflow-hidden"
          >
            <img 
              src="/logo-fav.png" 
              alt="Master Home Logo" 
              className="w-full h-full object-contain"
            />
          </motion.div>
          {/* Subtle glow behind logo */}
          <div className="absolute inset-0 bg-primary-500/20 blur-2xl rounded-full scale-50"></div>
        </div>

        {/* Name and Tagline */}
        <motion.div
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.2 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-display tracking-tight">
            Master Home Builders
          </h1>
          <p className="text-gray-500 mb-12 text-lg font-medium">
            Building Excellence, Delivering Trust.
          </p>
        </motion.div>

        {/* Links */}
        <div className="space-y-4">
          {links.map((link, index) => (
            <motion.a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index + 0.3 }}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all group lg:p-5"
            >
              <div className={`w-12 h-12 ${link.color} text-white rounded-2xl flex items-center justify-center shadow-lg transform group-hover:rotate-6 transition-transform flex-shrink-0`}>
                {link.icon}
              </div>
              <div className="flex flex-col items-start overflow-hidden">
                <span className="font-bold text-gray-800 text-lg group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {link.name}
                </span>
                <span className="text-xs text-gray-400 font-medium truncate w-full text-left">
                  {link.description}
                </span>
              </div>
              <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-5 h-5 text-primary-500" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Action Button */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="mt-12"
        >
          <a 
            href="mailto:masterhomedevelopers@gmail.com" 
            className="text-primary-600 font-bold hover:underline underline-offset-4"
          >
            Work with us
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-16 pb-8 text-gray-400 text-sm flex flex-col items-center gap-2"
        >
          <div className="w-8 h-[1px] bg-gray-200"></div>
          <p>© 2024 Master Home Builders</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Bio;
